import React, { useEffect, useRef, useState } from "react";
import {
  FilterList
} from "@mui/icons-material";
import {
  Button,
  Grid,
  styled,
  Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import useTokenRefresh from "../../../Hooks/useTokenRefresh";
import Footer from "../../../Components/Footer";
import VisualizationGraph from "../components/VisualizationGraph";
import ReportFilter from "../components/ReportFilter";
import ActiveFilters from "../components/ActiveFilters";
import ReportTable from "../components/ReportTable";
import useFilterState from "../hooks/useFilterState";
import useReportData from "../hooks/useReportData";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
const initialFilters = {
  sites: [],
  states: [],
  financialYear: null,
  startDateTime: "",
  endDateTime: "",
};

const buildQueryString = (filterValues) => {
  const queryParts = [];

  // Add sites parameters
  filterValues.sites?.forEach((site, index) => {
    queryParts.push(`stores[${index}]=${site.id}`);
  });

  // Add states parameters
  filterValues.states?.forEach((state, index) => {
    queryParts.push(`states[${index}]=${state.state}`);
  });

  // Add financial year
  if (filterValues.financialYear) {
    queryParts.push(`financial_year=${filterValues.financialYear.id}`);
  }

  // Add date range
  if (filterValues.startDateTime && filterValues.endDateTime) {
    queryParts.push(`start_date=${filterValues.startDateTime}`);
    queryParts.push(`end_date=${filterValues.endDateTime}`);
  }
  // Return with leading '&' if parameters exist
  return queryParts.length ? `&${queryParts.join('&')}` : '';
};

const Header = styled("div")(({ theme }) => ({
  "& h1": {
    color: theme.palette.primary.dark,
    margin: "0",
  },
}));
const PushNotificationsReport = (props) => {
  const { getTokenRefreshed: refresh, open: tokenOpen, overlay, setOpen: setTokenOpen, message: tokenMessage, messageState: tokenMessageState } = useTokenRefresh();
  const [filterOpen, setFilterOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messageState, setMessageState] = useState("");
  const {
    filters,
    appliedFilters,
    updateFilter,
    resetFilters,
    applyFilters,
    hasActiveFilters
  } = useFilterState(initialFilters);

  const { rows, title, loading, error, refetch } = useReportData(props?.request_name, appliedFilters, buildQueryString);

  useEffect(() => {
    if (error) {
      setMessageState("error");
      setMessage(error.response.data.message);
      setOpen(true);
    }
  }, [error]);
  
  const [columns, setColumns] = useState([
    { id: "total_notification_sent_to_litecard", name: "Total Notifications Sent To Members" },
    { id: "total_notification_campaigns", name: "Total Notification Campaings" },
  ]);

  useEffect(() => {
    let newColumns = [
      { id: "total_notification_sent_to_members", name: "Total Notifications Sent To Members" },
      { id: "total_notification_campaigns", name: "Total Notification Campaings" },
    ];

    // Add columns based on applied filters, prioritizing sites first
    if (appliedFilters.sites.length > 0) {
      newColumns.unshift({ id: "store_name", name: "Site" });
    }
    if (appliedFilters.states.length > 0) {
      // Add state column after site column if sites are also selected
      if (appliedFilters.sites.length > 0) {
        newColumns.splice(1, 0, { id: "state_name", name: "State" });
      } else {
        newColumns.unshift({ id: "state_name", name: "State" });
      }
    }

    // If no filters are applied, default to showing sites
    if (appliedFilters.sites.length === 0 && appliedFilters.states.length === 0) {
      newColumns.unshift({ id: "store_name", name: "Site" });
    }

    setColumns(newColumns);
  }, [appliedFilters.sites, appliedFilters.states]);

  const handleFilterSubmit = () => {
    applyFilters();
  };
  const handleFilterReset = () => {
    resetFilters();
  };

  const isRemoving = useRef(false);

  const removeFilter = (filterName) => {
    isRemoving.current = true;

    if (filterName === "dateRange") {
      // Clear both date properties
      updateFilter("startDateTime", "");
      updateFilter("endDateTime", "");
    } else {
      // Handle other filter types
      updateFilter(
        filterName,
        filterName === "sites"
          ? []
          : filterName === "states"
            ? []
            : filterName === "financialYear"
              ? null
              : undefined
      );
    }
  };

  useEffect(() => {
    if (isRemoving.current) {
      applyFilters();
      isRemoving.current = false;
    }
  }, [filters, applyFilters]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
    setTokenOpen(false);
  };
  const getGraphTitle = () => {
    if (appliedFilters.sites.length > 0) {
      return `Site-wise Push Notification ${title}`;
    } else if (appliedFilters.states.length > 0) {
      return `State-wise Push Notification ${title}`;
    } else {
      return `Site-wise Push Notification ${title}`; // Default to site-wise
    }
  };
  function transformStoreNotificationData(rawData) {
    if (!rawData || rawData.length === 0) return { categories: [], series: [] };

    // Determine category key based on applied filters
    let categoryKey;
    if (appliedFilters.sites.length > 0) {
      categoryKey = "store_name";
    } else if (appliedFilters.states.length > 0) {
      categoryKey = "state_name";
    } else {
      categoryKey = "store_name"; // Default to store_name
    }

    // Check if the determined category key exists in the data
    if (!rawData[0].hasOwnProperty(categoryKey)) {
      // Fallback to the other key if the preferred one doesn't exist
      categoryKey = categoryKey === "store_name" ? "state_name" : "store_name";
      if (!rawData[0].hasOwnProperty(categoryKey)) {
        console.error("Neither store_name nor state_name found in data");
        return { categories: [], series: [] };
      }
    }

    const categories = rawData.map((item) => item[categoryKey].trim());
    const excludeKeys = ["group_id", "send_type"];
    const numericKeys = Object.keys(rawData[0]).filter(
      (key) => key !== categoryKey && !excludeKeys.includes(key)
    );
    const series = numericKeys.map((key) => ({
      name: key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      data: rawData.map((item) => parseFloat(item[key])),
    }));
    return { categories, series };
  }
  const graphData = rows.slice(0, -1);
  const { categories, series } = transformStoreNotificationData(graphData);
  return (
    <div>
      <Grid container spacing={2}>
        <Grid item md={8} xs={12}>
          <Header>
            <h1>Push Notification Report</h1>
          </Header>
        </Grid>
        <Grid
          item
          md={4}
          xs={12}
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
        >
          <Button color="primary" variant="contained" onClick={() => setFilterOpen(!filterOpen)}>
            Filter <FilterList style={{ marginLeft: "5px" }} fontSize="small" />
          </Button>
        </Grid>
        <ReportFilter
          open={filterOpen}
          filters={filters}
          onFilterChange={updateFilter}
          onApply={handleFilterSubmit}
          onReset={handleFilterReset}
        />
        {hasActiveFilters && (
          <ActiveFilters
            filters={appliedFilters}
            onRemoveFilter={removeFilter}
          />
        )}
        <Grid item xs={12}>
          <VisualizationGraph
            title={getGraphTitle()}
            subtitle={"No. Of Push Notification"}
            categories={categories}
            series={series}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} mb={4}>
          <ReportTable
            columns={columns}
            rows={rows}
            loading={loading}
          />
        </Grid>
        <Footer overlay={overlay || props.overlayNew} />
      </Grid>
      <Snackbar
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={open || tokenOpen}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={messageState || tokenMessageState}
          sx={{ width: "100%" }}
        >
          {message || tokenMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};
export default PushNotificationsReport;